<template>
  <ComponentHeader title="Entity Engagement" subtitle="Configure sub-metrics and weights for composite engagement scoring">
    <button
      @click="saveConfig"
      :disabled="saving || !isValid"
      class="btn-primary"
    >
      {{ saving ? 'Saving...' : 'Save Configuration' }}
    </button>
  </ComponentHeader>

  <div class="p-4 space-y-6">
    <!-- Loading -->
    <LoadingIndicator v-if="loading" message="Loading configuration..." />

    <!-- Content -->
    <div v-else class="space-y-6">
      <!-- Weight Summary -->
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="font-medium text-gray-900">Weight Summary</h3>
            <p class="text-sm text-gray-500">Active metric weights must sum to 1.0</p>
          </div>
          <div
            class="text-2xl font-bold"
            :class="isWeightValid ? 'text-green-600' : 'text-amber-600'"
          >
            {{ totalWeight.toFixed(2) }}
          </div>
        </div>
        <div class="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            class="h-full transition-all duration-300"
            :class="isWeightValid ? 'bg-green-500' : totalWeight > 1 ? 'bg-red-500' : 'bg-amber-500'"
            :style="{ width: `${Math.min(totalWeight * 100, 100)}%` }"
          ></div>
        </div>
      </div>

      <!-- Sub-Metrics Table -->
      <div class="bg-white rounded-lg shadow">
        <div class="p-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 class="font-medium text-gray-900">Sub-Metrics</h3>
            <p class="text-sm text-gray-500">Metrics that contribute to the composite score</p>
          </div>
          <button
            @click="showAddModal = true"
            class="btn-primary-sm"
          >
            + Add Sub-Metric
          </button>
        </div>

        <div v-if="subMetrics.length === 0" class="p-8 text-center text-gray-500">
          No sub-metrics configured. Click "Add Sub-Metric" to add one.
        </div>

        <table v-else class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-28">Weight</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-28">Max Value</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-20">Active</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-24">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(metric, index) in subMetrics" :key="metric.key" :class="{ 'bg-gray-50': !metric.active }">
              <td class="px-4 py-3">
                <div class="font-medium text-gray-900">{{ metric.name }}</div>
                <div class="text-sm text-gray-500">{{ metric.key }}</div>
              </td>
              <td class="px-4 py-3">
                <input
                  v-model.number="metric.weight"
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  class="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-blue-dark focus:border-primary-500"
                  :disabled="!metric.active"
                />
              </td>
              <td class="px-4 py-3">
                <input
                  v-model.number="metric.max_value"
                  type="number"
                  min="0.1"
                  step="0.1"
                  class="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-blue-dark focus:border-primary-500"
                />
              </td>
              <td class="px-4 py-3 text-center">
                <input
                  v-model="metric.active"
                  type="checkbox"
                  class="h-4 w-4 text-primary-600 focus:ring-blue-dark border-gray-300 rounded"
                />
              </td>
              <td class="px-4 py-3 text-right">
                <button
                  @click="removeMetric(index)"
                  class="text-red-600 hover:text-red-800 text-sm"
                >
                  Remove
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Validation Errors -->
      <div v-if="validationErrors.length > 0" class="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h4 class="font-medium text-amber-800 mb-2">Configuration Issues:</h4>
        <ul class="list-disc list-inside text-sm text-amber-700">
          <li v-for="err in validationErrors" :key="err">{{ err }}</li>
        </ul>
      </div>

      <!-- Error -->
      <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {{ error }}
      </div>

      <!-- Success -->
      <div v-if="successMessage" class="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
        {{ successMessage }}
      </div>
    </div>

    <!-- Add Sub-Metric Modal -->
    <div v-if="showAddModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen px-4">
        <div class="fixed inset-0 bg-gray-500 opacity-75" @click="showAddModal = false"></div>
        <div class="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Add Sub-Metric</h3>

          <LoadingIndicator v-if="loadingCalculators" message="Loading calculators..." />

          <div v-else>
            <div class="max-h-64 overflow-y-auto border rounded-lg">
              <div
                v-for="calc in availableCalculators"
                :key="calc.key"
                @click="addMetric(calc)"
                class="p-3 hover:bg-primary-50 cursor-pointer border-b last:border-b-0"
              >
                <div class="font-medium text-gray-900">{{ calc.name }}</div>
                <div class="text-sm text-gray-500">{{ calc.description }}</div>
                <div class="text-xs text-gray-400 mt-1">
                  Key: {{ calc.key }} | Default max: {{ calc.default_max_value }}
                </div>
              </div>
              <div v-if="availableCalculators.length === 0" class="p-4 text-center text-gray-500">
                All available calculators have been added
              </div>
            </div>
          </div>

          <div class="mt-4 flex justify-end">
            <button
              @click="showAddModal = false"
              class="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

export default {
  name: 'EntityEngagementConfigManager',
  components: {
    ComponentHeader,
    LoadingIndicator
  },

  data() {
    return {
      loading: true,
      saving: false,
      loadingCalculators: false,
      error: null,
      successMessage: null,
      configId: null,
      subMetrics: [],
      allCalculators: [],
      showAddModal: false
    }
  },

  computed: {
    totalWeight() {
      return this.subMetrics
        .filter(m => m.active)
        .reduce((sum, m) => sum + (m.weight || 0), 0)
    },
    isWeightValid() {
      return Math.abs(this.totalWeight - 1.0) < 0.01
    },
    validationErrors() {
      const errors = []
      if (this.subMetrics.filter(m => m.active).length === 0) {
        errors.push('At least one sub-metric must be active')
      }
      if (!this.isWeightValid && this.subMetrics.filter(m => m.active).length > 0) {
        errors.push(`Active weights must sum to 1.0 (currently ${this.totalWeight.toFixed(2)})`)
      }
      const duplicates = this.subMetrics
        .map(m => m.key)
        .filter((key, i, arr) => arr.indexOf(key) !== i)
      if (duplicates.length > 0) {
        errors.push(`Duplicate keys: ${duplicates.join(', ')}`)
      }
      return errors
    },
    isValid() {
      return this.validationErrors.length === 0
    },
    usedKeys() {
      return this.subMetrics.map(m => m.key)
    },
    availableCalculators() {
      return this.allCalculators.filter(c => !this.usedKeys.includes(c.key))
    }
  },

  async mounted() {
    await this.loadData()
  },

  methods: {
    async loadData() {
      this.loading = true
      this.error = null

      try {
        // Load current config
        const configResponse = await axios.get('/api/engagement-score-config/')
        this.configId = configResponse.data.id
        this.subMetrics = (configResponse.data.sub_metrics || []).map(m => ({ ...m }))

        // Load available calculators
        await this.loadCalculators()
      } catch (err) {
        console.error('Error loading config:', err)
        this.error = 'Failed to load configuration: ' + (err.response?.data?.detail || err.message)
      } finally {
        this.loading = false
      }
    },

    async loadCalculators() {
      this.loadingCalculators = true
      try {
        const response = await axios.get('/api/engagement-score-config/available_calculators/')
        this.allCalculators = response.data
      } catch (err) {
        console.error('Error loading calculators:', err)
      } finally {
        this.loadingCalculators = false
      }
    },

    addMetric(calc) {
      this.subMetrics.push({
        key: calc.key,
        name: calc.name,
        weight: 0,
        max_value: calc.default_max_value,
        active: true
      })
      this.showAddModal = false
    },

    removeMetric(index) {
      this.subMetrics.splice(index, 1)
    },

    async saveConfig() {
      if (!this.isValid) return
      this.saving = true
      this.error = null
      this.successMessage = null

      try {
        await axios.patch(`/api/engagement-score-config/${this.configId}/`, {
          sub_metrics: this.subMetrics
        })
        this.successMessage = 'Configuration saved successfully'
        setTimeout(() => { this.successMessage = null }, 3000)
      } catch (err) {
        console.error('Error saving config:', err)
        this.error = 'Failed to save: ' + (err.response?.data?.detail || JSON.stringify(err.response?.data) || err.message)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
